import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectMembersInitializationStatus,
  selectGroupsInitializationStatus,
} from "../members-slice";
import {
  fetchMembers,
  fetchGroups,
} from "../slice-extensions/members-slice-extension";

/**
 * Loads the members/groups directory once if it hasn't been initialized yet.
 * A previously *failed* load (e.g. the token was expired) is retried once per
 * mount — enough to recover after a re-login, without retry-storming while the
 * failure persists. Call from a single-instance host (a page or a list
 * container), not from a row component that mounts many times.
 */
export function useEnsureDirectoryLoaded(): void {
  const dispatch = useAppDispatch();
  const membersInit = useAppSelector(selectMembersInitializationStatus);
  const groupsInit = useAppSelector(selectGroupsInitializationStatus);
  const retriedMembers = useRef(false);
  const retriedGroups = useRef(false);

  useEffect(() => {
    if (membersInit === "not-initialized") {
      dispatch(fetchMembers());
    } else if (membersInit === "failed" && !retriedMembers.current) {
      retriedMembers.current = true;
      dispatch(fetchMembers());
    }

    if (groupsInit === "not-initialized") {
      dispatch(fetchGroups());
    } else if (groupsInit === "failed" && !retriedGroups.current) {
      retriedGroups.current = true;
      dispatch(fetchGroups());
    }
  }, [membersInit, groupsInit, dispatch]);
}
